import { type ReactElement } from 'react'
import { isNotDefined, type UserProfileData } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Stack } from '@mantine/core'
import { getVisibleNotificationPreferenceOptions, useLoadedEntitlement, useProfile, useTranslation } from '@front/model'
import { NotReady, Page } from '@components'
import { NotificationSettings } from '../../notifications/NotificationSettings'
import { useNotificationPreferences } from './useNotificationPreferences'
import { CommunicationPreferencesGrid } from './CommunicationPreferencesGrid'

function CommunicationPreferences(): ReactElement {
  const { user, isLoading, error } = useProfile()

  if (isNotDefined(user)) {
    return <NotReady error={error} id='Profile' isLoading={isLoading} />
  }
  return <CommunicationPreferencesPage key={user.userId} user={user} />
}

const CommunicationPreferencesPage = ({ user }: { user: UserProfileData }): ReactElement => {
  const { t } = useTranslation()
  const { isTantalim, isSingleUser } = useProfile()
  const hasRecording = useLoadedEntitlement('recording')
  const options = getVisibleNotificationPreferenceOptions({ isTantalim, isSingleUser, hasRecording })
  const { preferences, isUpdating, setChannel } = useNotificationPreferences({
    userId: user.userId,
    initialPreferences: user.notificationPrefs,
    options,
  })

  return (
    <Page>
      <Page.Heading title={t('Notifications')} />
      <Page.Content scrollArea>
        <Stack gap='xl'>
          <NotificationSettings />
          <CommunicationPreferencesGrid
            disabled={isUpdating}
            options={options}
            preferences={preferences}
            onChange={setChannel}
          />
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/settings/communicationPreferences/')({
  component: CommunicationPreferences,
})
